import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, ArrowUpRight, Loader2 } from "lucide-react";
import { Overline } from "./Mark";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", market: "United States", enquiry_type: "Property marketing services", message: "" };
const TYPES = ["Property marketing services", "Lead generation", "Property enquiry", "Partnership", "Other"];
const MARKETS = ["United States", "United Kingdom", "Netherlands", "Multiple markets"];

const Label = ({ children }) => <span className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-slate-400">{children}</span>;

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, phone: form.phone || null });
      toast.success("Thank you — your enquiry has been received. Our team will be in touch.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your details and try again." : "Something went wrong. Please email support@vkenterpriseconsult.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-[#0B192C] py-24 lg:py-36" data-testid="contact-section">
      <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="min-w-0 lg:col-span-5">
          <Reveal>
            <Overline>Contact Us</Overline>
            <h2 className="font-serif-d mt-6 text-5xl font-medium leading-[1] text-white sm:text-6xl lg:text-7xl">Let's start a <em className="gold-text italic">conversation.</em></h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate-300">Interested in our property marketing services or have a property enquiry? Get in touch with our team — we look forward to hearing from you.</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12 space-y-4">
            <a href="mailto:support@vkenterpriseconsult.com" className="group flex items-center gap-5 rounded-2xl border border-[rgba(212,175,55,0.2)] p-5 transition-[border-color,background-color] duration-300 hover:border-[#D4AF37] hover:bg-[#0F1E36]" data-testid="contact-email-link">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-[#D4AF37] text-[#070E17]"><Mail className="h-5 w-5" /></span>
              <span className="min-w-0"><Label>Email</Label><span className="block break-all text-base text-white sm:text-lg">support@vkenterpriseconsult.com</span></span>
              <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-[#D4AF37] transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <a href="tel:+918796881958" className="group flex items-center gap-5 rounded-2xl border border-[rgba(212,175,55,0.2)] p-5 transition-[border-color,background-color] duration-300 hover:border-[#D4AF37] hover:bg-[#0F1E36]" data-testid="contact-phone-link">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-[#D4AF37] text-[#070E17]"><Phone className="h-5 w-5" /></span>
              <span><Label>Phone</Label><span className="block text-lg text-white">+91 87968 81958</span></span>
              <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-[#D4AF37] transition-transform duration-300 group-hover:rotate-45" />
            </a>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="min-w-0 lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="rounded-[32px] border border-[rgba(212,175,55,0.2)] bg-[#070E17]/70 p-6 backdrop-blur-xl sm:p-10" data-testid="contact-form">
            <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
              <label className="block"><Label>Full name *</Label><input required minLength={2} value={form.name} onChange={set("name")} className="field" placeholder="Your name" data-testid="contact-name-input" /></label>
              <label className="block"><Label>Email *</Label><input required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@email.com" data-testid="contact-email-input" /></label>
              <label className="block"><Label>Phone</Label><input value={form.phone} onChange={set("phone")} className="field" placeholder="+44 …" data-testid="contact-phone-input" /></label>
              <label className="block"><Label>Market *</Label>
                <select value={form.market} onChange={set("market")} className="field" data-testid="contact-market-select">{MARKETS.map((m) => <option key={m}>{m}</option>)}</select>
              </label>
              <label className="block sm:col-span-2"><Label>Enquiry type *</Label>
                <select value={form.enquiry_type} onChange={set("enquiry_type")} className="field" data-testid="contact-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              </label>
              <label className="block sm:col-span-2"><Label>Message *</Label><textarea required minLength={5} rows={4} value={form.message} onChange={set("message")} className="field resize-none" placeholder="Tell us about your property or project…" data-testid="contact-message-input" /></label>
            </div>
            <button disabled={busy} className="group mt-10 flex w-full items-center justify-between rounded-full bg-[#D4AF37] py-3 pl-8 pr-3 text-base font-medium text-[#070E17] transition-[background-color,transform] duration-300 hover:bg-[#E6C280] active:scale-[0.98] disabled:opacity-70" data-testid="contact-submit-btn">
              {busy ? "Sending…" : "Send enquiry"}
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#070E17] text-[#D4AF37] transition-transform duration-500 group-hover:rotate-45">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
              </span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
